import React, { useState, useRef, useEffect } from 'react';
import './AccountantDashboard.css';
import { FiHome, FiUsers, FiLogOut, FiMenu, FiX, FiFile } from 'react-icons/fi';
import ClientsAccountant from '../ClientsAccountant/ClientsAccountant';
import DocumentsAccountant from '../DocumentsAccountant/DocumentsAccountant';

export default function AccountantDashboard({ onSignOut }) {
  const [page, setPage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const [accountant, setAccountant] = useState(null);

  // Load accountant info on component mount
  useEffect(() => {
    const loadAccountantInfo = () => {
      // Try to get accountant info from different possible storage locations
      const accountantData = 
        JSON.parse(localStorage.getItem('accountant')) || 
        JSON.parse(localStorage.getItem('user')) || 
        JSON.parse(localStorage.getItem('userData')) ||
        JSON.parse(sessionStorage.getItem('user'));
      
      if (accountantData) {
        setAccountant(accountantData);
      }
    };
    
    loadAccountantInfo();
  }, []);

  // Get accountant initials for the avatar
  const getInitials = () => {
    if (!accountant || !accountant.name) return 'AC';
    
    const names = accountant.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return accountant.name.substring(0, 2).toUpperCase();
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    }
    if (profileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileOpen]);

  return (
    <div className="admin-dashboard-bg">
      <button className="hamburger-menu" onClick={toggleSidebar}>
        {sidebarOpen ? <FiX /> : <FiMenu />}
      </button>
      
      <aside className={`admin-dashboard-sidebar ${sidebarOpen ? 'active' : ''}`}>
        <div className="admin-dashboard-logo">FinBooks</div>
        
        <nav className="admin-dashboard-nav">
          <a 
            className={page === 'dashboard' ? 'active' : ''}
            href="#dashboard"
            onClick={(e) => { e.preventDefault(); handlePageChange('dashboard'); }}
          >
            <FiHome className="sidebar-icon" /> <span>Dashboard</span>
          </a>
          <a 
            className={page === 'clients' ? 'active' : ''}
            href="#clients"
            onClick={(e) => { e.preventDefault(); handlePageChange('clients'); }}
          >
            <FiUsers className="sidebar-icon" /> <span>Clients</span>
          </a>
          <a 
            className={page === 'documents' ? 'active' : ''}
            href="#documents"
            onClick={(e) => { e.preventDefault(); handlePageChange('documents'); }}
          >
            <FiFile className="sidebar-icon" /> <span>Documents</span>
          </a>
        </nav>
        
        <button className="admin-dashboard-signout" onClick={onSignOut}>
          <FiLogOut /> <span>Sign out</span>
        </button>
      </aside>
      
      <main className="admin-dashboard-main">
        <header className="admin-dashboard-header">
          <h1>
            {page === 'dashboard' && 'Dashboard'}
            {page === 'clients' && 'Client Management'}
            {page === 'documents' && 'Documents Management'}
          </h1>
          <div className="admin-dashboard-user" ref={profileRef} onClick={() => setProfileOpen((open) => !open)} style={{ position: 'relative', cursor: 'pointer' }}>
            <div className="admin-dashboard-user-avatar">{getInitials()}</div>
            <span className="admin-dashboard-user-name">{accountant?.name || 'Accountant'}</span>
            {profileOpen && (
              <div className="admin-dashboard-profile-dropdown">
                <div className="admin-dashboard-profile-info">
                  <div className="admin-dashboard-profile-avatar">{getInitials()}</div>
                  <div>
                    <div className="admin-dashboard-profile-name">{accountant?.name || 'Accountant'}</div>
                    <div className="admin-dashboard-profile-email">{accountant?.email || 'accountant@finbooks.com'}</div>
                    <div className="admin-dashboard-profile-status">
                      Status: <span className="profile-status-label">{accountant?.status || 'Active'}</span>
                    </div>
                  </div>
                </div>
                <div className="admin-dashboard-profile-actions">
                  <button 
                    className="admin-dashboard-profile-signout"
                    onClick={onSignOut}
                  >
                    <FiLogOut /> Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>
        
        <section className="admin-dashboard-content">
          {page === 'dashboard' && (
           <div className="empty-dashboard">
           <div className="empty-dashboard-title">Welcome, {accountant?.name || 'Accountant'}</div>
           <p className="empty-dashboard-description">
             Manage your clients and documents efficiently from this dashboard. 
             Use the navigation menu to access different sections of the accountant panel.
           </p>
           <div className="empty-dashboard-actions">
             <button 
               className="accountants-btn accountants-btn-primary"
               onClick={() => handlePageChange('clients')}
             >
               <FiUsers /> Manage clients
             </button>
             <button 
               className="accountants-btn accountants-btn-primary"
               onClick={() => handlePageChange('documents')}
             >
               <FiFile /> Manage documents
             </button>
           </div>
         </div>
          )}
          {page === 'clients' && <ClientsAccountant />}
          {page === 'documents' && <DocumentsAccountant />}
        </section>
      </main>
    </div>
  );
}